/**
 * Light/dark theme manager. The user preference ('light' | 'dark' | 'auto')
 * persists in localStorage; 'auto' (the default) follows the OS
 * prefers-color-scheme. The resolved theme is written to
 * <html data-theme="light|dark"> which styles.css keys its variables off.
 * index.html has a tiny inline script applying the same logic pre-CSS so a
 * dark-mode load doesn't flash white.
 */
export type ThemeMode = 'light' | 'dark' | 'auto';

const THEME_KEY = 'knytt-theme';
export const THEME_MODES: ThemeMode[] = ['auto', 'light', 'dark'];

const media = window.matchMedia('(prefers-color-scheme: dark)');

export function getThemeMode(): ThemeMode {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* localStorage unavailable */
  }
  return 'auto';
}

export function setThemeMode(mode: ThemeMode): void {
  try {
    if (mode === 'auto') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, mode);
  } catch {
    /* localStorage unavailable */
  }
  applyTheme();
}

/** Cycle auto → light → dark → auto (for the in-game pause button). */
export function cycleThemeMode(): ThemeMode {
  const next = THEME_MODES[(THEME_MODES.indexOf(getThemeMode()) + 1) % THEME_MODES.length];
  setThemeMode(next);
  return next;
}

export function themeLabel(mode: ThemeMode): string {
  return mode === 'auto' ? 'System' : mode === 'light' ? 'Light' : 'Dark';
}

function applyTheme(): void {
  const mode = getThemeMode();
  const dark = mode === 'dark' || (mode === 'auto' && media.matches);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
}

/** Apply the stored preference and track OS changes while in 'auto'. */
export function initTheme(): void {
  applyTheme();
  media.addEventListener('change', applyTheme);
}
